import api from './api';

export const CABO_TIPOS = [
  { value: 'drop', label: 'Drop' },
  { value: 'as', label: 'AS' },
  { value: 'asu', label: 'ASU' },
  { value: 'fig8', label: 'Fig8' },
  { value: 'opgw', label: 'OPGW' },
] as const;

export const CABO_PADROES_TUBO_LOOSE = [
  { value: 'abnt', label: 'ABNT' },
  { value: 'eia598a', label: 'EIA-598-A' },
] as const;

export const CABO_QTDE_FO_OPCOES = [1, 2, 4, 6, 8, 12, 24, 36, 48, 72, 144] as const;

export const CABO_QTDE_FO_OPCOES_ASU = [1, 2, 4, 6, 8, 12] as const;

export const CABO_FUNCOES_ESTRUTURAIS = [
  { value: 'backbone', label: 'Backbone' },
  { value: 'distribuicao', label: 'Distribuição' },
] as const;

export type CaboTipo = typeof CABO_TIPOS[number]['value'];
export type CaboFuncaoEstrutural = typeof CABO_FUNCOES_ESTRUTURAIS[number]['value'];
export type PadraoTuboLoose = typeof CABO_PADROES_TUBO_LOOSE[number]['value'];

export function getCaboTipoLabel(tipo?: CaboTipo | null): string {
  return CABO_TIPOS.find((item) => item.value === tipo)?.label ?? 'Não informado';
}

export function getPadraoTuboLooseLabel(padrao?: PadraoTuboLoose | null): string {
  return CABO_PADROES_TUBO_LOOSE.find((item) => item.value === padrao)?.label ?? 'ABNT';
}

export function getCaboFuncaoEstruturalLabel(funcao?: CaboFuncaoEstrutural | null): string {
  return CABO_FUNCOES_ESTRUTURAIS.find((item) => item.value === funcao)?.label ?? 'Não informado';
}

export interface CaboPonto {
  id: number;
  cabo_id: number;
  lat: number;
  lng: number;
  proximo_ponto_id: number | null;
  caixa_armario_id?: number | null;
  caixa_armario_tipo?: 'caixa' | 'armario' | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Cabo {
  id: number;
  titulo: string;
  pontos: CaboPonto[];
  qtde_fo: number;
  color: string;
  tipo?: CaboTipo | null;
  funcao_estrutural?: CaboFuncaoEstrutural | null;
  padrao_tubo_loose?: PadraoTuboLoose;
  qtde_tubo_loose?: number;
  escritorio_id?: number | null;
  fabricante?: string | null;
  lote?: string | null;
  observacoes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CaboFormData {
  titulo: string;
  pontos: Array<{ lat: number, lng: number }>;
  qtde_fo: number;
  color: string;
  tipo?: CaboTipo | null;
  funcao_estrutural?: CaboFuncaoEstrutural | null;
  padrao_tubo_loose?: PadraoTuboLoose;
  qtde_tubo_loose?: number;
  escritorio_id?: number | null;
  fabricante?: string | null;
  lote?: string | null;
  observacoes?: string | null;
}

export interface CabosResponse {
  cabos: Cabo[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

class CaboService {
  // Listar cabos com paginação e busca
  async getCabos(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<CabosResponse> {
    try {
      const response = await api.get('/cabos', { params });
      console.log('📡 caboService.getCabos - Resposta raw:', response);
      console.log('📊 caboService.getCabos - response.data:', response.data);

      // Verificar se os dados estão diretamente no response (interceptor)
      const responseAny = response as any;
      if (responseAny && responseAny.cabos && Array.isArray(responseAny.cabos)) {
        console.log(`✅ caboService - ${responseAny.cabos.length} cabos encontrados diretamente no response`);
        return {
          cabos: responseAny.cabos,
          totalItems: responseAny.totalItems || responseAny.cabos.length,
          totalPages: responseAny.totalPages || 1,
          currentPage: responseAny.currentPage || 1
        };
      }

      // Verificar estrutura normal em response.data
      let data = response.data;
      console.log('📊 caboService.getCabos - data extraído:', data);

      if (data && data.cabos && Array.isArray(data.cabos)) {
        console.log(`✅ caboService - ${data.cabos.length} cabos encontrados em response.data`);
        return data;
      }

      // Se a resposta tem uma estrutura nested, extrair os dados
      if (data && data.data && typeof data.data === 'object') {
        console.log('📊 caboService - Estrutura nested detectada');
        data = data.data;

        if (data && data.cabos && Array.isArray(data.cabos)) {
          console.log(`✅ caboService - ${data.cabos.length} cabos encontrados na estrutura nested`);
          return data;
        }
      }

      console.warn('⚠️ caboService - Estrutura de resposta inesperada:', { response, data });
      // Retornar estrutura vazia se não conseguir parsear
      return {
        cabos: [],
        totalItems: 0,
        totalPages: 0,
        currentPage: 1
      };
    } catch (error: any) {
      console.error('❌ caboService - Erro ao buscar cabos:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar cabos');
    }
  }

  // Buscar cabo por ID
  async getCaboById(id: number): Promise<Cabo> {
    try {
      const response = await api.get(`/cabos/${id}`);
      return response.data || response;
    } catch (error: any) {
      console.error('Erro ao buscar cabo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar cabo');
    }
  }

  // Buscar todos os cabos para exibir no mapa
  // cacheBust: quando true, adiciona timestamp para evitar cache HTTP (ex: após mover caixa/armário)
  async getCabosForMap(cacheBust = false): Promise<Cabo[]> {
    try {
      const params = cacheBust ? { _t: Date.now() } : undefined;
      const response = await api.get('/cabos/coordinates', { params });
      const data = Array.isArray(response) ? response : (response?.data ?? []);
      return data;
    } catch (error: any) {
      console.error('Erro ao buscar cabos para mapa:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar cabos para mapa');
    }
  }

  // Criar novo cabo
  async createCabo(data: CaboFormData): Promise<Cabo> {
    try {
      console.log('🚀 caboService: Enviando dados para API:', data);
      const response = await api.post('/cabos', data);
      console.log('📨 caboService: Resposta completa da API:', response);
      console.log('📊 caboService: response.data:', response.data);
      console.log('📊 caboService: response.status:', response.status);

      // Verificar se os dados estão em response.data ou diretamente no response
      let caboData = response.data;

      if (!caboData) {
        // Verificar se os dados estão diretamente no response
        const responseObj = response as any;
        if (responseObj.id && responseObj.titulo && responseObj.pontos) {
          console.log('📋 caboService: Dados encontrados diretamente no response');
          caboData = responseObj;
        }
      }

      if (!caboData) {
        console.error('❌ caboService: Nenhum dado encontrado na resposta!');
        throw new Error('Resposta vazia do servidor');
      }

      console.log('✅ caboService: Dados do cabo encontrados:', caboData);
      return caboData;
    } catch (error: any) {
      console.error('❌ caboService: Erro ao criar cabo:', error);
      console.error('❌ caboService: error.response:', error.response);
      throw new Error(error.response?.data?.error || 'Erro ao criar cabo');
    }
  }

  // Atualizar cabo
  async updateCabo(id: number, data: Partial<CaboFormData>): Promise<Cabo> {
    try {
      const response = await api.put(`/cabos/${id}`, data);
      return response.data || response;
    } catch (error: any) {
      console.error('Erro ao atualizar cabo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar cabo');
    }
  }

  // Deletar cabo
  async deleteCabo(id: number): Promise<void> {
    try {
      await api.delete(`/cabos/${id}`);
    } catch (error: any) {
      console.error('Erro ao deletar cabo:', error);
      throw new Error(error.response?.data?.error || 'Erro ao deletar cabo');
    }
  }

  // === Operações de Pontos (Linked List) ===

  // Listar pontos do cabo em ordem
  async getPontos(caboId: number): Promise<CaboPonto[]> {
    try {
      const response = await api.get(`/cabos/${caboId}/pontos`);
      const data = response.data || response;
      return data.pontos || data;
    } catch (error: any) {
      console.error('Erro ao buscar pontos:', error);
      throw new Error(error.response?.data?.error || 'Erro ao buscar pontos');
    }
  }

  // Adicionar ponto ao cabo (com inserção entre pontos)
  async addPonto(caboId: number, lat: number, lng: number, depois_de?: number): Promise<CaboPonto> {
    try {
      const body: any = { lat, lng };
      if (depois_de !== undefined) body.depois_de = depois_de;
      const response = await api.post(`/cabos/${caboId}/pontos`, body);
      return response.data || response;
    } catch (error: any) {
      console.error('Erro ao adicionar ponto:', error);
      throw new Error(error.response?.data?.error || 'Erro ao adicionar ponto');
    }
  }

  // Vincular ponto a caixa ou armário
  async vincularPonto(data: { ponto_id: number; cabo_id: number; caixa_id?: number; armario_id?: number }): Promise<{ success: boolean; ponto: CaboPonto }> {
    try {
      const response = await api.post('/cabos/vincular-ponto', data);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao vincular ponto:', error);
      throw new Error(error.response?.data?.error || 'Erro ao vincular ponto');
    }
  }

  // Atualizar lat/lng de um ponto
  async updatePonto(caboId: number, pontoId: number, data: { lat?: number; lng?: number }): Promise<CaboPonto> {
    try {
      const response = await api.put(`/cabos/${caboId}/pontos/${pontoId}`, data);
      return response.data || response;
    } catch (error: any) {
      console.error('Erro ao atualizar ponto:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar ponto');
    }
  }

  // Remover ponto
  async deletePonto(caboId: number, pontoId: number): Promise<void> {
    try {
      await api.delete(`/cabos/${caboId}/pontos/${pontoId}`);
    } catch (error: any) {
      console.error('Erro ao remover ponto:', error);
      throw new Error(error.response?.data?.error || 'Erro ao remover ponto');
    }
  }

  // Substituir todos os pontos (bulk)
  async bulkReplacePontos(caboId: number, pontos: Array<{ lat: number; lng: number }>): Promise<CaboPonto[]> {
    try {
      const response = await api.put(`/cabos/${caboId}/pontos/bulk`, { pontos });
      const data = response.data || response;
      return data.pontos || data;
    } catch (error: any) {
      console.error('Erro ao substituir pontos:', error);
      throw new Error(error.response?.data?.error || 'Erro ao substituir pontos');
    }
  }

  // Validar pontos do cabo
  validatePontos(pontos: Array<{ lat: number, lng: number }>): string | null {
    if (!Array.isArray(pontos) || pontos.length < 2) {
      return 'Mínimo de 2 pontos necessários';
    }

    for (const ponto of pontos) {
      if (typeof ponto.lat !== 'number' || typeof ponto.lng !== 'number') {
        return 'Cada ponto deve ter lat e lng como números';
      }

      if (ponto.lat < -90 || ponto.lat > 90) {
        return 'Latitude deve estar entre -90 e 90';
      }

      if (ponto.lng < -180 || ponto.lng > 180) {
        return 'Longitude deve estar entre -180 e 180';
      }
    }

    return null;
  }

  // Calcular distância total do cabo (aproximada)
  calculateDistance(pontos: Array<{ lat: number, lng: number }>): number {
    if (pontos.length < 2) return 0;

    let totalDistance = 0;

    for (let i = 1; i < pontos.length; i++) {
      const p1 = pontos[i - 1];
      const p2 = pontos[i];

      // Fórmula de Haversine para distância entre dois pontos
      const R = 6371; // Raio da Terra em km
      const dLat = this.toRad(p2.lat - p1.lat);
      const dLng = this.toRad(p2.lng - p1.lng);

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(p1.lat)) * Math.cos(this.toRad(p2.lat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      totalDistance += distance;
    }

    return totalDistance;
  }

  private toRad(value: number): number {
    return value * Math.PI / 180;
  }

  // Formatar distância para exibição
  formatDistance(distanceKm: number): string {
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1000)}m`;
    }
    return `${distanceKm.toFixed(2)}km`;
  }
}

export const caboService = new CaboService();